import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class FormattingService {
  groupByField(arr: any[], groupField: string) {
    return arr.reduce(
      (groups, item) => ({
        ...groups,
        [item[groupField]]: [...(groups[item[groupField]] || []), item],
      }),
      {}
    );
  }

  groupByFunction(arr: any[], callback: any) {
    return arr.reduce(
      (a, c) => (
        a[callback(c)] ? a[callback(c)].push(c) : (a[callback(c)] = [c]), a
      ),
      {}
    );
  }

  padLeft(str, padChar, totalSize) {
    if (!padChar) {
      return str;
    }
    let result = str;
    while (result.length < totalSize) {
      result = padChar + result;
    }
    return result;
  }

  padRight(str, padChar, totalSize) {
    if (!padChar) {
      return str;
    }
    let result = str;
    while (result.length < totalSize) {
      result = result + padChar;
    }
    return result;
  }

  getPropertyByPath(obj: any, path: string) {
    path = path.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
    path = path.replace(/^\./, ''); // strip a leading dot
    const a = path.split('.');
    for (let i = 0, n = a.length; i < n; ++i) {
      const k = a[i];
      if (k in obj) {
        obj = obj[k];
      } else {
        return;
      }
    }
    return obj;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sortBy(arr: unknown[], sortField: string, reverse = false): any[] {
    const result = [].concat(arr).sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (aValue == null && bValue == null) return 0;
      if (aValue != null && bValue == null) return 1;
      if (aValue == null && bValue != null) return -1;

      return this.compareValues(aValue, bValue);
    });

    return reverse ? result.reverse() : result;
  }

  /**
   * Sorts an array of objects by multiple fields using custom conventions.
   *
   * Sorting conventions:
   * - Prefix `!` before a field name reverses sorting.
   * - Prefix `*` before a field name sorts empty values (null, undefined, '') last.
   * - Prefix `*!` before a field name reverses sorting and puts empty values last.
   * - Multiple fields are sorted in order of appearance.
   *
   * DataFieldFormatting:
   * - If a field value is an array and the object contains a `__DataFieldFormatting` property
   *   with a matching field and a `__DisplayName`, the display name is used for sorting instead
   *   of the raw array value.
   *
   * Example configs:
   *   "date,time"      → date ascending with empty first, time ascending with empty first
   *   "!date,time"     → date descending with empty first, time ascending with empty first
   *   "date,!time"     → date ascending with empty first, time descending with empty first
   *   "*date,time"     → date ascending with empty last, time ascending with empty first
   *   "*!date,time"    → date descending with empty last, time ascending with empty first
   *   "!*date"         → Invalid notation (do not use)
   *
   * @param arr Array of objects to sort
   * @param sortFields Array of field names (with optional prefixes)
   * @param reverse If true, makes the default sort order descending
   * @returns The sorted array
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sortByConvention(
    arr: unknown[],
    sortFields: string[],
    reverse = false
  ): any[] {
    return arr.sort((a, b) => {
      for (const rawField of sortFields) {
        let sortField = rawField;
        let localSortAsc = !reverse;
        let emptyLast = false;

        if (sortField.startsWith('*!')) {
          emptyLast = true;
          localSortAsc = reverse;
          sortField = sortField.substring(2);
        } else if (sortField.startsWith('*')) {
          emptyLast = true;
          sortField = sortField.substring(1);
        } else if (sortField.startsWith('!')) {
          localSortAsc = reverse;
          sortField = sortField.substring(1);
        }

        let fieldA = a[sortField];
        let fieldB = b[sortField];

        if (fieldA != null && Array.isArray(fieldA)) {
          if (
            a['__DataFieldFormatting'] &&
            a['__DataFieldFormatting'].hasOwn(sortField) &&
            a['__DataFieldFormatting'][sortField].__DisplayName !== undefined
          ) {
            fieldA = a['__DataFieldFormatting'][sortField].__DisplayName;
          }
        }

        if (fieldB != null && Array.isArray(fieldB)) {
          if (
            b['__DataFieldFormatting'] &&
            b['__DataFieldFormatting'].hasOwn(sortField) &&
            b['__DataFieldFormatting'][sortField].__DisplayName !== undefined
          ) {
            fieldB = b['__DataFieldFormatting'][sortField].__DisplayName;
          }
        }

        const isEmptyA = fieldA == null || fieldA === '';
        const isEmptyB = fieldB == null || fieldB === '';

        if (emptyLast) {
          if (isEmptyA && !isEmptyB) return 1;
          if (!isEmptyA && isEmptyB) return -1;
          if (isEmptyA && isEmptyB) continue;
        } else {
          if (isEmptyA && !isEmptyB) return -1;
          if (!isEmptyA && isEmptyB) return 1;
          if (isEmptyA && isEmptyB) continue;
        }

        const valA = String(fieldA).toLowerCase();
        const valB = String(fieldB).toLowerCase();

        if (valA < valB) return localSortAsc ? -1 : 1;
        if (valA > valB) return localSortAsc ? 1 : -1;
      }

      return 0;
    });
  }

  private compareValues(aValue: unknown, bValue: unknown): number {
    return typeof aValue === 'number' && typeof bValue === 'number'
      ? aValue - bValue
      : aValue
          .toString()
          .localeCompare(bValue.toString(), undefined, { numeric: true });
  }
}
